import { useState, useEffect } from 'react';
import { UseFormSetValue, Path, PathValue } from 'react-hook-form';
import { useDebounce } from '@/hooks/general';
import { dictionaryService } from '@/services/api';

interface UseZipLocationsProps<T extends object> {
    zipValue: string;
    countryCodeValue: string;
    setValue: UseFormSetValue<Partial<T>>;
    stateFieldName?: Path<Partial<T>>;
    cityFieldName?: Path<Partial<T>>;
}

export const useZipLocations = <T extends object>({
    zipValue,
    countryCodeValue,
    setValue,
    stateFieldName = 'stateCode' as Path<Partial<T>>,
}: UseZipLocationsProps<T>) => {
    const [statesList, setStatesList] = useState<string[]>([]);
    const [citiesList, setCitiesList] = useState<string[]>([]);
    const [zipUpdated, setZipUpdated] = useState(false);

    const onZipChange = () => setZipUpdated(true);

    const debouncedCountryCodeValue = useDebounce(countryCodeValue);
    const debouncedZipValue = useDebounce(zipValue);
    const debouncedZipUpdated = useDebounce(zipUpdated);

    useEffect(() => {
        void (async () => {
            if (debouncedCountryCodeValue && debouncedZipValue && debouncedZipUpdated) {
                const data = await dictionaryService.getZipLocations(
                    debouncedCountryCodeValue,
                    debouncedZipValue,
                );
                const { states, cities } = data.reduce(
                    (acc, item) => {
                        acc.states.add(item.stateCode);
                        acc.cities.add(item.city);
                        return acc;
                    },
                    { states: new Set<string>(), cities: new Set<string>() },
                );
                setStatesList([...states]);
                setCitiesList([...cities]);
            }
        })();
    }, [debouncedCountryCodeValue, debouncedZipValue, debouncedZipUpdated]);

    useEffect(() => {
        if (statesList.length === 1 && debouncedZipUpdated && stateFieldName) {
            setValue(stateFieldName, statesList[0] as PathValue<Partial<T>, Path<Partial<T>>>);
        }
    }, [statesList, setValue, stateFieldName, debouncedZipUpdated]);

    const isMoreThanOneState = statesList && statesList?.length > 1;

    return { statesList, citiesList, isMoreThanOneState, onZipChange };
};
